import { useState, useCallback, useRef } from 'react';

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'progress';
  progress?: number; // 0-100 for progress toasts
}

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const addToast = useCallback((message: string, type: Toast['type'] = 'info', autoRemove = true) => {
    const id = Date.now().toString();
    const toast: Toast = { id, message, type };

    setToasts((prev) => [...prev, toast]);

    // Auto-remove after 3 seconds (except progress toasts)
    if (autoRemove && type !== 'progress') {
      const timeout = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        timeoutRefs.current.delete(id);
      }, 3000);
      timeoutRefs.current.set(id, timeout);
    }

    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timeout = timeoutRefs.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutRefs.current.delete(id);
    }
  }, []);

  const success = useCallback((message: string) => addToast(message, 'success'), [addToast]);
  const error = useCallback((message: string) => addToast(message, 'error'), [addToast]);
  const info = useCallback((message: string) => addToast(message, 'info'), [addToast]);

  // Progress toast - shows loading state with optional progress bar
  const progress = useCallback((message: string, percent?: number) => {
    const id = Date.now().toString();
    const toast: Toast = { id, message, type: 'progress', progress: percent };
    setToasts((prev) => [...prev, toast]);
    return id;
  }, []);

  // Update progress of an existing toast
  const updateProgress = useCallback((id: string, percent: number, message?: string) => {
    setToasts((prev) =>
      prev.map((t) =>
        t.id === id
          ? { ...t, progress: percent, ...(message ? { message } : {}) }
          : t
      )
    );
  }, []);

  // Complete a progress toast - converts to success and auto-removes
  const completeProgress = useCallback((id: string, message?: string) => {
    setToasts((prev) =>
      prev.map((t) =>
        t.id === id
          ? { ...t, type: 'success' as const, progress: undefined, ...(message ? { message } : {}) }
          : t
      )
    );

    // Auto-remove after 2 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 2000);
  }, []);

  // Fail a progress toast - converts to error and auto-removes
  const failProgress = useCallback((id: string, message?: string) => {
    setToasts((prev) =>
      prev.map((t) =>
        t.id === id
          ? { ...t, type: 'error' as const, progress: undefined, ...(message ? { message } : {}) }
          : t
      )
    );

    // Auto-remove after 3 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  return {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    info,
    progress,
    updateProgress,
    completeProgress,
    failProgress,
  };
}
